/**
 * AI AGENTS SERVICE (APPMOR)
 * 
 * Este archivo ahora es un thin wrapper sobre @boosting/ai.
 * Mantiene compatibilidad hacia atrás con el código existente.
 * 
 * @deprecated Importar directamente desde '@boosting/ai' para nuevos desarrollos
 * 
 * @example
 * ```typescript
 * // ❌ DEPRECATED - Usar solo para código legacy
 * import { aiAgentsService } from '@/core/services/ai';
 * 
 * // ✅ NUEVO - Importar desde @boosting/ai
 * import { createAgentsConfigService } from '@boosting/ai';
 * ```
 */

import { db } from '@/core/config/firebase';
import {
  createAgentsConfigService,
  AgentsConfigService,
  type AIAgent,
  type AIAgentType,
  type CreateAIAgentDTO,
  type UpdateAIAgentDTO,
} from '@boosting/ai';

// Re-export types for backward compatibility
export type { AIAgent, AIAgentType, CreateAIAgentDTO, UpdateAIAgentDTO };

// Re-export from appmor model for backward compatibility
export { DEFAULT_AI_AGENTS } from '@boosting/ai';

// ============================================
// ADAPTER CLASS (Legacy Compatibility)
// ============================================

/**
 * Wrapper que adapta el Firestore de appmor al servicio centralizado
 * 
 * @deprecated Usar createAgentsConfigService directamente
 */
class AIAgentsServiceAdapter {
  private service: AgentsConfigService;

  constructor() {
    // Adapter para el Firestore de appmor
    const firestoreAdapter = {
      collection: (path: string) => {
        const colRef = db.collection(path);
        return {
          doc: (id: string) => {
            const docRef = colRef.doc(id);
            return {
              get: async () => {
                const snap = await docRef.get();
                return {
                  id: snap.id,
                  exists: snap.exists,
                  data: () => snap.data(),
                };
              },
              update: (data: Record<string, unknown>) => docRef.update(data),
              delete: () => docRef.delete(),
            };
          },
          add: async (data: Record<string, unknown>) => {
            const ref = await colRef.add(data);
            return { id: ref.id };
          },
          where: (field: string, op: string, value: unknown) => {
            let query = colRef.where(field, op as FirebaseFirestore.WhereFilterOp, value);
            return {
              where: (f: string, o: string, v: unknown) => {
                query = query.where(f, o as FirebaseFirestore.WhereFilterOp, v);
                return this;
              },
              limit: (n: number) => {
                query = query.limit(n);
                return {
                  get: async () => {
                    const snap = await query.get();
                    return {
                      empty: snap.empty,
                      docs: snap.docs.map(d => ({
                        id: d.id,
                        exists: d.exists,
                        data: () => d.data(),
                      })),
                    };
                  },
                };
              },
              get: async () => {
                const snap = await query.get();
                return {
                  empty: snap.empty,
                  docs: snap.docs.map(d => ({
                    id: d.id,
                    exists: d.exists,
                    data: () => d.data(),
                  })),
                };
              },
            };
          },
          get: async () => {
            const snap = await colRef.get();
            return {
              empty: snap.empty,
              docs: snap.docs.map(d => ({
                id: d.id,
                exists: d.exists,
                data: () => d.data(),
              })),
            };
          },
        };
      },
    };

    this.service = createAgentsConfigService({
      firestore: firestoreAdapter as any,
      collection: 'ai_agents',
    });
  }

  // Delegate all methods to the centralized service
  getAgent(type: AIAgentType) {
    return this.service.getAgent(type);
  }

  getPrompt(type: AIAgentType, variables: Record<string, string> = {}) {
    return this.service.getPrompt(type, variables);
  }

  getAIConfig(type: AIAgentType) {
    return this.service.getAIConfig(type);
  }

  getAllAgents() {
    return this.service.getAllAgents();
  }

  createAgent(data: CreateAIAgentDTO) {
    return this.service.createAgent(data);
  }

  updateAgent(agentId: string, updates: UpdateAIAgentDTO) {
    return this.service.updateAgent(agentId, updates);
  }

  deleteAgent(agentId: string) {
    return this.service.deleteAgent(agentId);
  }

  recordUsage(type: AIAgentType) {
    return this.service.recordUsage(type);
  }

  initializeDefaultAgents() {
    return this.service.initializeDefaultAgents();
  }

  syncMissingAgents() {
    return this.service.syncMissingAgents();
  }

  invalidateCache(type?: AIAgentType) {
    return this.service.invalidateCache(type);
  }

  renderPrompt(template: string, variables: Record<string, string>) {
    return this.service.renderPrompt(template, variables);
  }
}

/**
 * @deprecated Usar createAgentsConfigService desde @boosting/ai
 */
export const aiAgentsService = new AIAgentsServiceAdapter();
